import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useProgressNavigation } from "@/components/progress-bar";
import { currentChartPatientId } from "@/lib/patient-route";
import { UserType } from "@/types/enums/user-type";
import type { SidebarOperation } from "@/types/sidebar-session";
import { useTask } from "@/api/provider/tasks/tasks-query";
import { selectPatientSidebar } from "@/features/sidebar/patient-sidebar";
import { TaskSidebarCommand, selectTaskSidebar, taskSidebarDefinition } from "@/features/sidebar/task-sidebar";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";

/** Keep task/chart navigation in sync, even when the task tab is not selected. */
export function useTaskSidebarPatientSync() {
  const $ = _c(18);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.toString();
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const chartUrl = `${pathname}?${t0}`;
  const previousChartUrl = useRef(chartUrl);
  const {
    taskId,
    chartSyncPending,
    selectionVersion
  } = useRightSidebarStore(selectTaskSidebar);
  const ownerScope = useRightSidebarStore(_temp);
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const t1 = !!taskId;
  let t2;
  if ($[2] !== t1) {
    t2 = {
      enabled: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: task,
    isFetching: isTaskFetching
  } = useTask(taskId ?? "", t2);
  const sidebarPatientId = useRightSidebarStore(_temp2);
  const previousSidebarPatientId = useRef(sidebarPatientId);
  const pendingPatientOpen = useRef(null);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = currentChartPatientId();
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const previousPatientId = useRef(t3);
  const taskNavigationPatientId = useRef(null);
  const sidebarSyncedVersion = useRef(null);
  const requestedSelectionVersion = useRef(null);
  const pendingOperation = useRef(null);
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => pendingOperation.current?.dispose();
    t5 = [];
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  const previousScope = useRef(ownerScope);
  let t6;
  let t7;
  if ($[7] !== chartSyncPending || $[8] !== chartUrl || $[9] !== isTaskFetching || $[10] !== navigateWithProgress || $[11] !== ownerScope || $[12] !== selectionVersion || $[13] !== sidebarPatientId || $[14] !== task || $[15] !== taskId) {
    t6 = () => {
      if (previousScope.current !== ownerScope) {
        previousScope.current = ownerScope;
        pendingOperation.current?.dispose();
        pendingOperation.current = null;
        taskNavigationPatientId.current = null;
        sidebarSyncedVersion.current = null;
        requestedSelectionVersion.current = null;
        pendingPatientOpen.current = null;
        previousPatientId.current = currentChartPatientId();
        previousChartUrl.current = chartUrl;
        previousSidebarPatientId.current = sidebarPatientId;
      }
      const store = useRightSidebarStore.getState();
      const closeTaskSidebar = () => store.close(taskSidebarDefinition.id);
      const markChartSynced = () => store.open(taskSidebarDefinition, {
        command: TaskSidebarCommand.ACCEPT,
        selectionVersion
      });
      const patientId = currentChartPatientId();
      const firstPatientTabOpened = !previousSidebarPatientId.current && !!sidebarPatientId;
      previousSidebarPatientId.current = sidebarPatientId;
      if (pendingPatientOpen.current?.selectionVersion !== selectionVersion || pendingPatientOpen.current?.patientId !== sidebarPatientId || !taskId) {
        pendingPatientOpen.current = null;
      }
      if (firstPatientTabOpened && sidebarPatientId && taskId) {
        pendingPatientOpen.current = {
          patientId: sidebarPatientId,
          selectionVersion
        };
      }
      if (pendingPatientOpen.current && !isTaskFetching && task?.id === taskId) {
        const openedPatientId = pendingPatientOpen.current.patientId;
        pendingPatientOpen.current = null;
        if (task.patient?.id !== openedPatientId) {
          closeTaskSidebar();
          return;
        }
      }
      const patientChanged = !!patientId && previousPatientId.current !== patientId;
      previousPatientId.current = patientId;
      const chartUrlChanged = previousChartUrl.current !== chartUrl;
      previousChartUrl.current = chartUrl;
      if (taskNavigationPatientId.current && (!patientId || chartUrlChanged && !patientChanged)) {
        taskNavigationPatientId.current = null;
        if (!chartSyncPending) {
          closeTaskSidebar();
          return;
        }
      }
      if (patientChanged) {
        const isTaskNavigation = taskNavigationPatientId.current === patientId;
        taskNavigationPatientId.current = null;
        if (patientId && taskId && !isTaskNavigation) {
          closeTaskSidebar();
          return;
        }
      }
      if (!chartSyncPending || !taskId) {
        sidebarSyncedVersion.current = null;
      }
      if (isTaskFetching) {
        return;
      }
      if (chartSyncPending && task?.id === taskId && sidebarSyncedVersion.current !== selectionVersion) {
        sidebarSyncedVersion.current = selectionVersion;
        if (task.patient) {
          store.open(taskSidebarDefinition, {
            command: TaskSidebarCommand.SYNC_PATIENT,
            selectionVersion,
            patientTiny: {
              ...task.patient,
              firstName: task.patient.firstName ?? "",
              type: UserType.PATIENT
            }
          });
        }
      }
      if (taskNavigationPatientId.current) {
        return;
      }
      if (!patientId || !chartSyncPending || !task || task.id !== taskId) {
        return;
      }
      const taskPatientId = task.patient?.id;
      if (patientId && taskPatientId && taskPatientId !== patientId) {
        if (requestedSelectionVersion.current === selectionVersion) {
          return;
        }
        requestedSelectionVersion.current = selectionVersion;
        pendingOperation.current?.dispose();
        const operation = store.beginOperation(taskSidebarDefinition.id);
        pendingOperation.current = operation;
        const scope = store.environment.ownerScope;
        const isCurrent = () => {
          const live = useRightSidebarStore.getState();
          const selection = selectTaskSidebar(live);
          return operation.isCurrent() && live.environment.ownerScope === scope && selection.selectionVersion === selectionVersion && selection.taskId === taskId && currentChartPatientId() === patientId;
        };
        navigateWithProgress(`/patient/${taskPatientId}`, true, {
          replacePendingNavigation: true,
          onCancelNavigation: () => {
            if (isCurrent()) {
              store.open(taskSidebarDefinition, {
                command: TaskSidebarCommand.REJECT,
                selectionVersion
              });
            }
            operation?.dispose();
          },
          onBeforeNavigate: () => {
            if (!isCurrent() || currentChartPatientId() !== patientId) {
              return false;
            }
            taskNavigationPatientId.current = taskPatientId;
            markChartSynced();
            operation?.dispose();
          }
        });
      } else {
        markChartSynced();
      }
    };
    t7 = [chartUrl, ownerScope, sidebarPatientId, taskId, chartSyncPending, selectionVersion, task, isTaskFetching, navigateWithProgress];
    $[7] = chartSyncPending;
    $[8] = chartUrl;
    $[9] = isTaskFetching;
    $[10] = navigateWithProgress;
    $[11] = ownerScope;
    $[12] = selectionVersion;
    $[13] = sidebarPatientId;
    $[14] = task;
    $[15] = taskId;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useEffect(t6, t7);
}
function _temp2(state_0) {
  return selectPatientSidebar(state_0).patientId;
}
function _temp(state) {
  return state.environment.ownerScope;
}